'use client';
import React from 'react';
import { ArrowLeft, ArrowRight, Info } from 'lucide-react';
import type { DesignWizardState } from './DesignWizardClient';

interface StepDimensionsProps {
  state: DesignWizardState;
  onChange: (updates: Partial<DesignWizardState>) => void;
  onBack: () => void;
  onNext: () => void;
}

export default function StepDimensions({ state, onChange, onBack, onNext }: StepDimensionsProps) {
  const length = parseFloat(state.length) || 0;
  const width = parseFloat(state.width) || 0;
  const area = length * width;

  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!state.length || parseFloat(state.length) <= 0) errs.length = 'يجب إدخال قيمة أكبر من صفر';
    if (!state.width || parseFloat(state.width) <= 0) errs.width = 'يجب إدخال قيمة أكبر من صفر';
    if (!state.ceilingHeight || parseFloat(state.ceilingHeight) <= 0) errs.ceilingHeight = 'يجب إدخال ارتفاع السقف';
    if (parseFloat(state.length) > 500) errs.length = 'قيمة غير منطقية';
    if (parseFloat(state.width) > 500) errs.width = 'قيمة غير منطقية';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  const inputClass = (field: string) =>
    `w-full px-4 py-3 rounded-xl border-2 bg-card text-foreground font-tabular text-base transition-all duration-200 focus:outline-none focus:border-primary focus:shadow-sm ${
      errors[field] ? 'border-danger' : 'border-border'
    }`;

  return (
    <div className="slide-up">
      <div className="text-center mb-8">
        <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
          ما أبعاد المكان؟
        </h2>
        <p className="text-muted-foreground">
          أدخل أبعاد {state.roomType?.nameAr} بالمتر
        </p>
      </div>

      <div className="wizard-card p-6 lg:p-8 max-w-2xl mx-auto">
        {/* Area Preview */}
        {area > 0 && (
          <div className="bg-accent rounded-xl p-4 mb-6 flex items-center justify-between fade-in">
            <span className="text-secondary font-semibold">مساحة المكان</span>
            <span className="text-2xl font-bold text-primary font-tabular">{area.toFixed(1)} m²</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Length */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1">
              طول المكان <span className="text-danger">*</span>
            </label>
            <p className="text-xs text-muted-foreground mb-2">بالمتر</p>
            <input
              type="number"
              min="0.1"
              max="500"
              step="0.1"
              placeholder="مثال: 5.0"
              value={state.length}
              onChange={(e) => onChange({ length: e.target.value })}
              className={inputClass('length')}
            />
            {errors.length && <p className="text-danger text-xs mt-1">{errors.length}</p>}
          </div>

          {/* Width */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1">
              عرض المكان <span className="text-danger">*</span>
            </label>
            <p className="text-xs text-muted-foreground mb-2">بالمتر</p>
            <input
              type="number"
              min="0.1"
              max="500"
              step="0.1"
              placeholder="مثال: 4.0"
              value={state.width}
              onChange={(e) => onChange({ width: e.target.value })}
              className={inputClass('width')}
            />
            {errors.width && <p className="text-danger text-xs mt-1">{errors.width}</p>}
          </div>

          {/* Ceiling Height */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1">
              ارتفاع السقف <span className="text-danger">*</span>
            </label>
            <p className="text-xs text-muted-foreground mb-2">بالمتر — عادةً بين 2.5 و4 م</p>
            <input
              type="number"
              min="1.5"
              max="20"
              step="0.1"
              placeholder="مثال: 2.8"
              value={state.ceilingHeight}
              onChange={(e) => onChange({ ceilingHeight: e.target.value })}
              className={inputClass('ceilingHeight')}
            />
            {errors.ceilingHeight && <p className="text-danger text-xs mt-1">{errors.ceilingHeight}</p>}
          </div>

          {/* Working Height */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1">
              ارتفاع مستوى العمل
              <span className="text-muted-foreground font-normal"> (اختياري)</span>
            </label>
            <p className="text-xs text-muted-foreground mb-2">ارتفاع سطح العمل — عادةً 0.85 م</p>
            <input
              type="number"
              min="0"
              max="2"
              step="0.05"
              placeholder="0.85"
              value={state.workingHeight}
              onChange={(e) => onChange({ workingHeight: e.target.value })}
              className={inputClass('workingHeight')}
            />
          </div>
        </div>

        {/* Info note */}
        <div className="flex items-start gap-2 mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
          <Info size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-700">
            القيم بالمتر فقط. المساحة تُحسب تلقائياً من الطول × العرض.
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-8 max-w-2xl mx-auto">
        <button onClick={onBack} className="btn-secondary px-6 py-3 rounded-xl flex items-center gap-2">
          <ArrowRight size={18} />
          <span>السابق</span>
        </button>
        <button onClick={handleNext} className="btn-primary px-8 py-3 rounded-xl flex items-center gap-2">
          <span>التالي</span>
          <ArrowLeft size={18} />
        </button>
      </div>
    </div>
  );
}